import { ScheduleShiftType, Shift, User } from "@shiftly/shared/src/schema";
import { TRIAL_ROLE } from "@shiftly/shared/src/constants";
import { DateTime } from "luxon";

export const formatCurrency = (amount: number, currencySymbol: '$' | '£' = '$'): string => {
  return new Intl.NumberFormat(currencySymbol === '$' ? 'en-US' : 'en-GB', {
    style: 'currency',
    currency: currencySymbol === '$' ? 'USD' : 'GBP'
  }).format(amount);
};

export const formatCurrencyGBP = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount);
};

export function formatDateTime(date: DateTime): string {
  return date.toFormat('dd/MM/yyyy hh:mm a');
};

export const formatTime = (timeString: string) => {
  // Parse hours and minutes directly from the time string
  const [hours, minutes] = timeString.split(':');
  const numHours = parseInt(hours, 10);
  const numMinutes = parseInt(minutes, 10);

  if (isNaN(numHours) || isNaN(numMinutes)) {
    return timeString; // Return original string if parsing fails
  }

  // Convert 24-hour format to 12-hour format directly
  const period = numHours >= 12 ? ' PM' : ' AM';
  const displayHours = numHours % 12 || 12;
  return `${displayHours}:${numMinutes.toString().padStart(2, '0')}${period}`;
};

export const formatDateToUTCTime = (date: DateTime) => {
  return formatTime(date.hour + ":" + date.minute);
};

export const formatShiftDate = (date: DateTime) => {
  return date.toFormat("d MMM yyyy 'UTC'");
};

export const formatTimeRange = (start: DateTime, end: DateTime) => {
  const startTime = start.toFormat('HH:mm');
  const endTime = end.toFormat('HH:mm');
  return `${startTime}-${endTime}`;
};

export const formatDate = (date: DateTime) => {
  return date.toFormat('dd/MM/yyyy')
};

export const getUserName = (users: User[], user_id: string) => {
  const user = users.find(u => u.id === user_id);
  if (!user) return 'Unassigned';
  if (user.roles?.includes(TRIAL_ROLE)) {
    return `${user.full_name} (Trial ${user.pay_class})`;
  }
  return `${user.full_name}`;
};

export const getUserRoles = (users: User[], user_id: string): string[] => {
  const user = users.find(u => u.id === user_id);
  return user?.roles || [];
};

export const formatUserName = (user: Partial<User>, showPayClass: boolean = true) => {
  if (user.roles?.includes(TRIAL_ROLE)) {
    return `${user.full_name} ${showPayClass ? `(Trial ${user.pay_class})` : ''}`;
  }
  return `${user.full_name} ${showPayClass ? `(${user.pay_class})` : ''}`;
}

export const getTypeLabel = (type: ScheduleShiftType) => {
  switch (type) {
    case 'MASS_DM':
      return 'Mass DM';
    case 'FP_MASS_DM':
      return 'FP Mass DM';
    case 'UK_POSTS':
      return 'UK Posts';
    case 'FP_POSTS':
      return 'FP Posts';
    default:
      return type;
  }
};

// Helper function to convert array of arrays to TSV
export const convertToTSV = (data: (string | number)[][]): string => {
  return data.map(row => row.join('\t')).join('\n');
};

export const formatShiftDataForCopy = (shift: Shift, userName: string, modelName: string, ppv_earned: number, noppv_earned: number, userRoles?: string[]) => {
  if (!shift) return '';

  // Determine the business day based on 6am cutoff
  let shiftDate = shift.start_date_time;
  if (shiftDate.hour < 6) {
    // If shift starts before 6am, it belongs to the previous business day
    shiftDate = shiftDate.minus({ day: 1 });
  }

  const formattedDate = shiftDate
    .setZone('utc')
    .toFormat('dd/MM');

  // Format the time span (HH:MM - HH:MM)
  const timeSpan =
    shift.start_date_time
      .setZone('utc')
      .toFormat('hh:mm a')
      .toUpperCase() +
    ' - ' +
    shift.end_date_time
      .setZone('utc')
      .toFormat('hh:mm a')
      .toUpperCase();

  const hours = Number(((shift.end_date_time.toMillis() - shift.start_date_time.toMillis()) / (1000 * 60 * 60)).toFixed(1));
  const isTrialRole = userRoles?.includes(TRIAL_ROLE);
  let shiftType = "Regular Shift";
  if (shift.shift_code.includes("OT")) {
    shiftType = "OT";
  } else if (shift.shift_code.includes("COVER")) {
    shiftType = "COVER";
  } else if (isTrialRole) {
    shiftType = "Trial Day ";
  } else if (shift.shift_code.includes("LAUNCH")) {
    shiftType = "LAUNCH";
  }

  return `${shiftType}
${userName} ${formattedDate}
${modelName} ${timeSpan} (${hours} hours)
Total PPV: ${formatCurrency(ppv_earned || 0)}
Total No PPV: ${formatCurrency(noppv_earned || 0)}`;
};